import { ref, onUnmounted } from 'vue';
import { socketService } from '@/services/socket.service';
import { CHANNELS, POS_EVENTS } from '@/services/socket.events';

/**
 * Per-session POS socket — subscribes to ONE dining session's channel
 * (private-pos.{token}). Use on /pos/sessions/:token (CartPanel).
 *
 * For branch-wide staff notifications (help / check-bill across all tables)
 * use usePOSBranchSocket instead.
 *
 * @param {object}   options
 * @param {number}   options.token            session token (route param)
 * @param {Function} [options.onItemUpdated]  ({ order_id, item_id, product_name, state }) => void
 */
export function usePOSSessionSocket({ token, onOrderStateUpdated, onItemUpdated }) {
  const channel = CHANNELS.posSession(token);

  const isConnected = ref(socketService.isConnected());

  // ─── Track connection state ───────────────────────────────────────────────
  const offChange = socketService.onConnectionChange((isUp) => { isConnected.value = isUp; });

  // ─── Subscribe to private channel ────────────────────────────────────────
  socketService.subscribe(channel);

  const offOrderStateUpdated = socketService.on(channel, POS_EVENTS.ORDER_STATE_UPDATED, (data) => {
    onOrderStateUpdated?.(data);
  });

  const offItemUpdated = socketService.on(channel, POS_EVENTS.ITEM_UPDATED, (data) => {
    onItemUpdated?.(data);
  });

  // ─── Cleanup ──────────────────────────────────────────────────────────────
  onUnmounted(() => {
    offChange();
    offOrderStateUpdated();
    offItemUpdated();
    socketService.unsubscribe(channel);
  });

  return {
    isConnected,
  };
}
